import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/** Standalone section file — same pattern as section-rendering-product-card */
const QUICK_ADD_SECTION_ID = 'section-rendering-quick-add';

/**
 * Section Rendering API — returns only the quick-add section HTML.
 * @param {string} productPageUrl
 * @returns {URL}
 */
function buildQuickAddSectionUrl(productPageUrl) {
  const url = new URL(productPageUrl, window.location.origin);
  url.searchParams.set('section_id', QUICK_ADD_SECTION_ID);
  return url;
}

/**
 * Fallback — full quick-add template page when the section API fails.
 * @param {string} productPageUrl
 * @returns {URL}
 */
function buildQuickAddFallbackUrl(productPageUrl) {
  const url = new URL(productPageUrl, window.location.origin);
  url.searchParams.set('view', 'quick-add');
  return url;
}

/**
 * @param {string} responseText
 * @returns {Document | null}
 */
function parseQuickAddResponse(responseText) {
  const trimmed = responseText.trim();
  if (!trimmed || trimmed === '{"main":null}' || trimmed.includes('"main":null')) return null;

  if (trimmed.startsWith('{')) {
    const json = /** @type {Record<string, string | null>} */ (JSON.parse(trimmed));
    const html = json[QUICK_ADD_SECTION_ID] ?? json.main ?? Object.values(json)[0];
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html');
  }

  return new DOMParser().parseFromString(trimmed, 'text/html');
}

/**
 * @param {string} productPageUrl
 * @param {AbortSignal} signal
 * @returns {Promise<Document | null>}
 */
async function fetchQuickAddDocument(productPageUrl, signal) {
  for (const buildUrl of [buildQuickAddSectionUrl, buildQuickAddFallbackUrl]) {
    const response = await fetch(buildUrl(productPageUrl).toString(), { signal });
    if (!response.ok) continue;

    const doc = parseQuickAddResponse(await response.text());
    if (doc?.querySelector('[data-product-grid-content]')) return doc;
  }

  return null;
}

/** @type {HTMLElement | null} */
let activeQuickAddButton = null;

/** @type {HTMLElement | null} */
let lastClickedQuickAddButton = null;

/** @type {ReturnType<typeof setTimeout> | null} */
let quickAddLoadingTimeout = null;

/** @param {Element | null | undefined} btn */
function clearQuickAddButtonLoading(btn) {
  if (!(btn instanceof HTMLElement)) return;
  btn.removeAttribute('data-loading');
  btn.removeAttribute('disabled');
  btn.querySelector('.quick-add__spinner')?.remove();
}

function clearAllQuickAddButtonLoading() {
  clearQuickAddButtonLoading(activeQuickAddButton);
  clearQuickAddButtonLoading(lastClickedQuickAddButton);
  activeQuickAddButton = null;
  lastClickedQuickAddButton = null;
  if (quickAddLoadingTimeout) {
    clearTimeout(quickAddLoadingTimeout);
    quickAddLoadingTimeout = null;
  }
}

/**
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
function getQuickAddButtonFromEvent(event) {
  if (!(event.target instanceof HTMLElement)) return null;
  const button = event.target.closest('.quick-add__button');
  return button instanceof HTMLElement ? button : null;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );

    if (productCard) return productCard.productPageUrl;

    const productLink = hotspotProduct?.getHotspotProductLink();
    let productUrl = '';
    if (productLink instanceof HTMLAnchorElement) productUrl = productLink.href;
    if (productLink instanceof HTMLInputElement) productUrl = productLink.value;


    if (!productUrl) return '';

    const url = new URL(productUrl, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // Prefetch on product-card hover (fires much earlier than quick-add button hover)
    this.#attachPrefetchListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Attaches the prefetch mouseenter listener to the closest product-card,
   * or falls back to this element. Called once on connect and after cache clears.
   */
  #attachPrefetchListener() {
    const target = this.closest('product-card') ?? this;
    target.addEventListener('mouseenter', this.#prefetch, { once: true });
  }

  /**
   * Silently prefetches and caches product content on card hover,
   * so the modal opens instantly when the user clicks.
   */
  #prefetch = async () => {
    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl)) return;

    try {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          this.#cachedContent.set(currentUrl, /** @type {Element} */(gridElement.cloneNode(true)));
        }
      }
    } catch {
      // Silently ignore prefetch errors — click will retry
    }
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    // Re-attach prefetch listener on product-card since cache was cleared
    this.#attachPrefetchListener();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    const button = getQuickAddButtonFromEvent(event);
    if (button) activeQuickAddButton = button;

    const modalContent = document.getElementById('quick-add-modal-content');

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (productGrid) {
      try {
        const freshContent = productGrid.cloneNode(true);
        this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
        this.#openQuickAddModal();
      } catch (error) {
        console.error('QuickAdd: Cached path failed', error);
      } finally {
        clearQuickAddButtonLoading(button);
        if (activeQuickAddButton === button) activeQuickAddButton = null;
      }
    } else {
      // UNCACHED PATH: open modal immediately with a loading skeleton, then
      // populate it once the fetch returns — no waiting before the user sees anything.

      // Show modal right away with a centred spinner so the user has instant feedback.
      if (modalContent) {
        modalContent.setAttribute('aria-busy', 'true');
        modalContent.innerHTML = '<div class="quick-add__modal-loader" aria-hidden="true"></div>';
      }
      this.#openQuickAddModal();

      try {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);

        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }

        if (productGrid) {
          const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
          this.updateQuickAddModal(freshContent);
          this.#updateVariantPicker(productGrid);

          if (modalContent) {
            modalContent.removeAttribute('aria-busy');
          }
        }
      } catch (error) {
        console.error('QuickAdd: Loading failed', error);
        if (modalContent) {
          modalContent.removeAttribute('aria-busy');
          modalContent.innerHTML = '';
        }
      } finally {
        clearQuickAddButtonLoading(button);
        if (activeQuickAddButton === button) activeQuickAddButton = null;
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });

    // Reset horizontal slideshow on first visual frame so it never falsely inherits scroll Left
    const scroller = dialogComponent.querySelector('slideshow-slides');
    const slideshowComponent = /** @type {any} */(dialogComponent.querySelector('slideshow-component'));
    const progressBar = /** @type {HTMLElement | null} */(dialogComponent.querySelector('.quick-add-progress-bar'));

    if (scroller) scroller.scrollTo({ left: 0, behavior: 'instant' });
    if (slideshowComponent && typeof slideshowComponent.select === 'function') {
      slideshowComponent.select(0, undefined, { animate: false });
    }
    if (progressBar && scroller && scroller.children.length > 0) {
      progressBar.style.width = `${(1 / scroller.children.length) * 100}%`;
    }

    // Explicitly update progress safely after layout confirms the reset
    requestAnimationFrame(() => {
      // @ts-ignore
      if (typeof dialogComponent.updateProgressFn === 'function') {
        // @ts-ignore
        dialogComponent.updateProgressFn();
      }
    });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
  if (!productPageUrl) return null;

  this.#abortController?.abort();
  const controller = new AbortController(); // local ref
  this.#abortController = controller;

  try {
    return await fetchQuickAddDocument(productPageUrl, controller.signal);
  } catch (error) {
    if (error.name === 'AbortError') return null;
    throw error;

  } finally {
    if (this.#abortController === controller) {
      this.#abortController = null;
    }
  }
}

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // if (isMobileBreakpoint()) {
    //   const productDetails = productGrid.querySelector('.product-details');
    //   const productFormComponent = productGrid.querySelector('product-form-component');
    //   const variantPicker = productGrid.querySelector('variant-picker');
    //   const productPrice = productGrid.querySelector('product-price');
    //   const productTitle = document.createElement('a');
    //   productTitle.textContent = this.dataset.productTitle || '';

    //   // Make product title as a link to the product page
    //   productTitle.href = this.productPageUrl;

    //   const productHeader = document.createElement('div');
    //   productHeader.classList.add('product-header');

    //   productHeader.appendChild(productTitle);
    //   if (productPrice) {
    //     productHeader.appendChild(productPrice);
    //   }
    //   productGrid.appendChild(productHeader);

    //   if (variantPicker) {
    //     productGrid.appendChild(variantPicker);
    //   }
    //   if (productFormComponent) {
    //     productGrid.appendChild(productFormComponent);
    //   }

    //   productDetails?.remove();
    // }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) {
      // Fire-and-forget: the progress bar initialises in the background so the
      // modal can open immediately without waiting for the slideshow to settle.
      dialogComponent.initQuickAddProgressBar();
    }
  }


  /**
   * Updates the quick-add button state based on whether a swatch is selected

   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, (event) => {
      this.#updateProductTitleLink(/** @type {CustomEvent} */(event));
      this.initQuickAddProgressBar();
    });

    this.addEventListener('quick-add:load-product', (event) => {
      if (event instanceof CustomEvent && event.detail.url) {
        this.loadProduct(event.detail.url);
      }
    });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    clearAllQuickAddButtonLoading();

    // Reset slideshow immediately when closed so the next open starts at 0 without a visual jump
    const scroller = /** @type {HTMLElement | null} */ (this.querySelector('slideshow-slides'));
    const slideshowComponent = /** @type {import('./slideshow').Slideshow} */ (this.querySelector('slideshow-component'));
    const progressBar = /** @type {HTMLElement | null} */ (this.querySelector('.quick-add-progress-bar'));

    if (scroller) {
      scroller.scrollLeft = 0;
    }
    if (slideshowComponent && typeof slideshowComponent.select === 'function') {
      slideshowComponent.select(0, undefined, { animate: false });
    }
    if (progressBar && scroller && scroller.children.length > 0) {
      progressBar.style.width = `${(1 / scroller.children.length) * 100}%`;
    }

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  /**
   * Initializes or updates the custom scroll progress bar for the Quick Add modal
   */
  async initQuickAddProgressBar() {
    const scroller = /** @type {HTMLElement | null} */ (this.querySelector('slideshow-slides'));
    const container = /** @type {HTMLElement | null} */ (this.querySelector('slideshow-component'));
    if (!scroller || !container) return;

    // Always reset arrow visibility first — morph preserves inline styles from previous
    // products, so arrows hidden for a ≤2-slide product would stay hidden otherwise.
    const arrows = container.querySelectorAll('slideshow-arrows, .slideshow__arrow');
    arrows.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.removeProperty('display');
      }
    });

    const prevBtn = /** @type {HTMLButtonElement | null} */ (container.querySelector('[ref="previous"], .slideshow-control--previous'));
    const nextBtn = /** @type {HTMLButtonElement | null} */ (container.querySelector('[ref="next"], .slideshow-control--next'));

    // Hide arrows and skip progress bar if there are 2 or fewer slides
    if (scroller.children.length <= 2) {
      arrows.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
      return;
    }

    // Modal always opens at slide 0:
    //   - previous → disabled (no slide before the first)
    //   - next     → enabled  (there are more slides ahead)
    // The Slideshow's scroll handler (sync → current setter) will naturally
    // disable/enable these as the user scrolls, so we only need to set
    // the correct *initial* state here.
    if (prevBtn) prevBtn.disabled = true;
    if (nextBtn) nextBtn.disabled = false;

    // Await select(0) so the Slideshow's internal #current resets to 0 and the
    // scroller's scrollLeft is at 0 before we calculate the progress bar position.
    // select() is async — not awaiting it means updateProgressFn reads a stale scrollLeft.
    const slideshowComponent = /** @type {import('./slideshow').Slideshow} */ (container);
    if (typeof slideshowComponent.select === 'function') {
      await slideshowComponent.select(0, undefined, { animate: false });
    }
    // Explicitly force scrollLeft = 0 as a safety net (covers the case where
    // select() resolves before the browser has committed the scroll).
    scroller.scrollLeft = 0;

    let progressBar = /** @type {HTMLElement | null} */ (container.querySelector('.quick-add-progress-bar'));

    if (!progressBar) {
      const progressEl = container.querySelector('.quick-add-progress');
      if (progressEl) {
        progressEl.remove();
      }

      const progressHTML = `
        <div class="quick-add-progress">
          <div class="quick-add-progress-bar"></div>
        </div>
      `;
      container.insertAdjacentHTML('beforeend', progressHTML);
      progressBar = container.querySelector('.quick-add-progress-bar');
    }

    if (!progressBar) return;

    // Clean up previous listeners and observers if they exist on this dialog instance
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.updateProgressFn && this._currentScroller) {
      this._currentScroller.removeEventListener('scroll', this.updateProgressFn);
    }

    // Track the new scroller
    this._currentScroller = scroller;

    this.updateProgressFn = () => {
      // Prevent calculating when the dialog is hidden (clientWidth 0)
      if (scroller.clientWidth === 0 || scroller.scrollWidth === 0) return;

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (maxScroll <= 0) {
        if (scroller.children.length > 2) {
          progressBar.style.width = `${(1 / scroller.children.length) * 100}%`;
        } else {
          progressBar.style.width = '100%';
        }
        return;
      }

      const startPercentage = (scroller.clientWidth / scroller.scrollWidth) * 100;
      const scrollRatio = Math.max(0, Math.min(1, scroller.scrollLeft / maxScroll));
      const percentage = startPercentage + (scrollRatio * (100 - startPercentage));

      progressBar.style.width = `${percentage}%`;
    };

    scroller.addEventListener('scroll', this.updateProgressFn, { passive: true });

    // Immediately set the bar to the starting position (slide 0) to avoid a
    // flash of the previous product's final progress value.
    if (scroller.clientWidth > 0 && scroller.scrollWidth > 0) {
      const startPercentage = (scroller.clientWidth / scroller.scrollWidth) * 100;
      progressBar.style.width = `${startPercentage}%`;
    } else if (scroller.children.length > 0) {
      const startPercentage = (1 / scroller.children.length) * 100;
      progressBar.style.width = `${startPercentage}%`;
    } else {
      progressBar.style.width = '0%';
    }

    // Use ResizeObserver to catch when the dialog becomes fully visible/painted
    // and recalculate (handles the case where clientWidth is 0 on first call).
    this.resizeObserver = new ResizeObserver(() => {
      if (this.updateProgressFn) {
        this.updateProgressFn();
      }
    });
    this.resizeObserver.observe(scroller);

    // Trigger one final evaluation after the next paint to confirm layout is settled.
    requestAnimationFrame(this.updateProgressFn);
  }

  /**
   * Loads a new product into the quick add modal
   * @param {string} url - The URL of the product to load
   */
  async loadProduct(url) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.setAttribute('aria-busy', 'true');
    modalContent.style.opacity = '0.5';
    modalContent.style.pointerEvents = 'none';

    try {
      let doc = await fetchQuickAddDocument(url, new AbortController().signal);

      if (!doc) {
        throw new Error('QuickAdd: Content not found in the response');
      }

      const newContent = doc.querySelector('[data-product-grid-content]');
      if (newContent) {
        morph(modalContent, newContent);
        this.initQuickAddProgressBar();
      } else {
        console.error('QuickAdd: Content not found in the response', doc);
      }
    } catch (error) {
      console.error('Error loading product in quick add:', error);
    } finally {
      modalContent.removeAttribute('aria-busy');
      modalContent.style.opacity = '';
      modalContent.style.pointerEvents = '';
    }
  }
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

// import { morph } from '@theme/morph';
// import { Component } from '@theme/component';
// import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
// import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
// import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
// import VariantPicker from '@theme/variant-picker';

// export class QuickAddComponent extends Component {
//   /** @type {AbortController | null} */
//   #abortController = null;
//   /** @type {Map<string, Element>} */
//   #cachedContent = new Map();
//   /** @type {AbortController} */
//   #cartUpdateAbortController = new AbortController();

//   get productPageUrl() {
//     const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
//     const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
//       this.closest('product-hotspot-component')
//     );

//     if (productCard) return productCard.productPageUrl;

//     const productLink = hotspotProduct?.getHotspotProductLink();
//     let productUrl = '';
//     if (productLink instanceof HTMLAnchorElement) productUrl = productLink.href;
//     if (productLink instanceof HTMLInputElement) productUrl = productLink.value;


//     if (!productUrl) return '';

//     const url = new URL(productUrl, window.location.origin);

//     if (url.searchParams.has('variant')) {
//       return url.toString();
//     }

//     const selectedVariantId = this.#getSelectedVariantId();
//     if (selectedVariantId) {
//       url.searchParams.set('variant', selectedVariantId);
//     }

//     return url.toString();
//   }

//   /**
//    * Gets the currently selected variant ID from the product card
//    * @returns {string | null} The variant ID or null
//    */
//   #getSelectedVariantId() {
//     const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
//     return productCard?.getSelectedVariantId() || null;
//   }

//   connectedCallback() {
//     super.connectedCallback();

//     mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
//     document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
//       signal: this.#cartUpdateAbortController.signal,
//     });
//     document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();

//     mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
//     this.#abortController?.abort();
//     this.#cartUpdateAbortController.abort();
//     document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
//   }

//   /**
//    * Clears the cached content when cart is updated
//    */
//   #handleCartUpdate = () => {
//     this.#cachedContent.clear();
//   };

//   /**
//    * Re-renders the variant picker in the quick-add modal.
//    * @param {Element} newHtml - The element to re-render.
//    */
//   #updateVariantPicker(newHtml) {
//     const modalContent = document.getElementById('quick-add-modal-content');
//     if (!modalContent) return;
//     const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
//     if (!variantPicker) return;
//     variantPicker.updateVariantPicker(newHtml);
//   }

//   /**
//    * Handles quick add button click
//    * @param {Event} event - The click event
//    */
//   handleClick = async (event) => {
//     event.preventDefault();

//     const currentUrl = this.productPageUrl;
//     if (!currentUrl) return;

//     // Check if we have cached content for this URL
//     let productGrid = this.#cachedContent.get(currentUrl);

//     if (!productGrid) {
//       // Fetch and cache the content
//       const urlWithView = new URL(currentUrl, window.location.origin);
//       urlWithView.searchParams.set('view', 'quick-add');
//       const html = await this.fetchProductPage(urlWithView.toString());
//       if (html) {
//         const gridElement = html.querySelector('[data-product-grid-content]');
//         if (gridElement) {
//           // Cache the cloned element to avoid modifying the original
//           productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
//           this.#cachedContent.set(currentUrl, productGrid);
//         }
//       }
//     }

//     if (productGrid) {
//       // Use a fresh clone from the cache
//       const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
//       await this.updateQuickAddModal(freshContent);
//       this.#updateVariantPicker(productGrid);
//     }

//     this.#openQuickAddModal();
//   };

//   #resetScroll() {
//     const dialogComponent = document.getElementById('quick-add-dialog');
//     if (!(dialogComponent instanceof QuickAddDialog)) return;

//     const productDetails = dialogComponent.querySelector('.product-details');
//     const productMedia = dialogComponent.querySelector('.product-information__media');
//     productDetails?.scrollTo({ top: 0, behavior: 'instant' });
//     productMedia?.scrollTo({ top: 0, behavior: 'instant' });
//   }

//   /** @param {QuickAddDialog} dialogComponent */
//   #stayVisibleUntilDialogCloses(dialogComponent) {
//     this.toggleAttribute('stay-visible', true);

//     dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
//       once: true,
//     });
//   }

//   #openQuickAddModal = () => {
//     const dialogComponent = document.getElementById('quick-add-dialog');
//     if (!(dialogComponent instanceof QuickAddDialog)) return;

//     this.#stayVisibleUntilDialogCloses(dialogComponent);

//     dialogComponent.showDialog();

//     // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
//     // Waiting until the open animation starts seemed to be the most reliable metric here.
//     const dialog = dialogComponent.refs?.dialog;
//     if (!dialog) return;
//     dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
//   };

//   #closeQuickAddModal = () => {
//     const dialogComponent = document.getElementById('quick-add-dialog');
//     if (!(dialogComponent instanceof QuickAddDialog)) return;

//     dialogComponent.closeDialog();
//   };

//   /**
//    * Fetches the product page content
//    * @param {string} productPageUrl - The URL of the product page to fetch
//    * @returns {Promise<Document | null>}
//    */
//   async fetchProductPage(productPageUrl) {
//     if (!productPageUrl) return null;

//     // We use this to abort the previous fetch request if it's still pending.
//     this.#abortController?.abort();
//     this.#abortController = new AbortController();

//     try {
//       const response = await fetch(productPageUrl, {
//         signal: this.#abortController.signal,
//       });

//       if (!response.ok) {
//         throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
//       }

//       const responseText = await response.text();
//       const html = new DOMParser().parseFromString(responseText, 'text/html');

//       return html;
//     } catch (error) {
//       if (error.name === 'AbortError') {
//         return null;
//       } else {
//         throw error;
//       }
//     } finally {
//       this.#abortController = null;
//     }
//   }

//   /**
//    * Re-renders the variant picker.
//    * @param {Element} productGrid - The product grid element
//    */
//   async updateQuickAddModal(productGrid) {
//     const modalContent = document.getElementById('quick-add-modal-content');

//     if (!productGrid || !modalContent) return;

//     // if (isMobileBreakpoint()) {
//     //   const productDetails = productGrid.querySelector('.product-details');
//     //   const productFormComponent = productGrid.querySelector('product-form-component');
//     //   const variantPicker = productGrid.querySelector('variant-picker');
//     //   const productPrice = productGrid.querySelector('product-price');
//     //   const productTitle = document.createElement('a');
//     //   productTitle.textContent = this.dataset.productTitle || '';

//     //   // Make product title as a link to the product page
//     //   productTitle.href = this.productPageUrl;

//     //   const productHeader = document.createElement('div');
//     //   productHeader.classList.add('product-header');

//     //   productHeader.appendChild(productTitle);
//     //   if (productPrice) {
//     //     productHeader.appendChild(productPrice);
//     //   }
//     //   productGrid.appendChild(productHeader);

//     //   if (variantPicker) {
//     //     productGrid.appendChild(variantPicker);
//     //   }
//     //   if (productFormComponent) {
//     //     productGrid.appendChild(productFormComponent);
//     //   }

//     //   productDetails?.remove();
//     // }

//     morph(modalContent, productGrid);

//     this.#syncVariantSelection(modalContent);
//     const dialogComponent = document.getElementById('quick-add-dialog');
//     if (dialogComponent instanceof QuickAddDialog) {
//       dialogComponent.initQuickAddProgressBar();
//     }
//   }


//   /**
//    * Updates the quick-add button state based on whether a swatch is selected

//    * @param {VariantSelectedEvent} event - The variant selected event
//    */
//   #updateQuickAddButtonState(event) {
//     if (!(event.target instanceof HTMLElement)) return;
//     if (event.target.closest('product-card') !== this.closest('product-card')) return;
//     const productOptionsCount = this.dataset.productOptionsCount;
//     const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
//     this.setAttribute('data-quick-add-button', quickAddButton);
//   }

//   /**
//    * Syncs the variant selection from the product card to the modal
//    * @param {Element} modalContent - The modal content element
//    */
//   #syncVariantSelection(modalContent) {
//     const selectedVariantId = this.#getSelectedVariantId();
//     if (!selectedVariantId) return;

//     // Find and check the corresponding input in the modal
//     const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
//     for (const input of modalInputs) {
//       if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
//         input.checked = true;
//         input.dispatchEvent(new Event('change', { bubbles: true }));
//         break;
//       }
//     }
//   }
// }

// if (!customElements.get('quick-add-component')) {
//   customElements.define('quick-add-component', QuickAddComponent);
// }

// class QuickAddDialog extends DialogComponent {
//   #abortController = new AbortController();

//   connectedCallback() {
//     super.connectedCallback();

//     this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
//     this.addEventListener(ThemeEvents.variantUpdate, (event) => {
//       this.#updateProductTitleLink(/** @type {CustomEvent} */(event));
//       this.initQuickAddProgressBar();
//     });

//     this.addEventListener('quick-add:load-product', (event) => {
//       if (event instanceof CustomEvent && event.detail.url) {
//         this.loadProduct(event.detail.url);
//       }
//     });

//     this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();

//     this.#abortController.abort();
//     this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
//   }

//   /**
//    * Closes the dialog
//    * @param {CartUpdateEvent} event - The cart update event
//    */
//   handleCartUpdate = (event) => {
//     if (event.detail.data.didError) return;
//     this.closeDialog();
//   };

//   #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
//     const anchorElement = /** @type {HTMLAnchorElement} */ (
//       event.detail.data.html?.querySelector('.view-product-title a')
//     );
//     const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
//     const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

//     if (!anchorElement) return;

//     if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
//     if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
//   };

//   #handleDialogClose = () => {
//     const iosVersion = getIOSVersion();
//     /**
//      * This is a patch to solve an issue with the UI freezing when the dialog is closed.
//      * To reproduce it, use iOS 16.0.
//      */
//     if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

//     requestAnimationFrame(() => {
//       /** @type {HTMLElement | null} */
//       const grid = document.querySelector('#ResultsList [product-grid-view]');
//       if (grid) {
//         const currentWidth = grid.getBoundingClientRect().width;
//         grid.style.width = `${currentWidth - 1}px`;
//         requestAnimationFrame(() => {
//           grid.style.width = '';
//         });
//       }
//     });
//   };

//   /**
//    * Initializes or updates the custom scroll progress bar for the Quick Add modal
//    */
//   initQuickAddProgressBar() {
//     const scroller = /** @type {HTMLElement | null} */ (this.querySelector('slideshow-slides'));
//     const container = /** @type {HTMLElement | null} */ (this.querySelector('slideshow-component'));
//     if (!scroller || !container) return;

//     // Fast-path: hide arrows and skip progress bar if there are 2 or fewer slides
//     if (scroller.children.length <= 2) {
//       const arrows = container.querySelectorAll('slideshow-arrows, .slideshow__arrow');
//       arrows.forEach((el) => {
//         if (el instanceof HTMLElement) {
//           el.style.setProperty('display', 'none', 'important');
//         }
//       });
//       return;
//     }

//     let progressBar = /** @type {HTMLElement | null} */ (container.querySelector('.quick-add-progress-bar'));

//     if (!progressBar) {
//       const progressEl = container.querySelector('.quick-add-progress');
//       if (progressEl) {
//         progressEl.remove();
//       }

//       const progressHTML = `
//         <div class="quick-add-progress">
//           <div class="quick-add-progress-bar"></div>
//         </div>
//       `;
//       container.insertAdjacentHTML('beforeend', progressHTML);
//       progressBar = container.querySelector('.quick-add-progress-bar');
//     }

//     if (!progressBar) return;

//     // Clean up previous listeners and observers if they exist on this dialog instance
//     if (this.resizeObserver) {
//       this.resizeObserver.disconnect();
//     }
//     if (this.updateProgressFn && this._currentScroller) {
//       this._currentScroller.removeEventListener('scroll', this.updateProgressFn);
//     }

//     // Track the new scroller
//     this._currentScroller = scroller;

//     this.updateProgressFn = () => {
//       // Prevent calculating when the dialog is hidden (clientWidth 0)
//       if (scroller.clientWidth === 0 || scroller.scrollWidth === 0) return;

//       const maxScroll = scroller.scrollWidth - scroller.clientWidth;
//       if (maxScroll <= 0) {
//         progressBar.style.width = '100%';
//         return;
//       }

//       const startPercentage = (scroller.clientWidth / scroller.scrollWidth) * 100;
//       const scrollRatio = Math.max(0, Math.min(1, scroller.scrollLeft / maxScroll));
//       const percentage = startPercentage + (scrollRatio * (100 - startPercentage));

//       progressBar.style.width = `${percentage}%`;
//     };

//     scroller.addEventListener('scroll', this.updateProgressFn, { passive: true });

//     // Use ResizeObserver to reliably catch when the dialog becomes visible/painted
//     this.resizeObserver = new ResizeObserver(() => {
//       if (this.updateProgressFn) {
//         this.updateProgressFn();
//       }
//     });
//     this.resizeObserver.observe(scroller);

//     // Force an immediate evaluation
//     requestAnimationFrame(this.updateProgressFn);
//   }

//   /**
//    * Loads a new product into the quick add modal
//    * @param {string} url - The URL of the product to load
//    */
//   async loadProduct(url) {
//     const modalContent = document.getElementById('quick-add-modal-content');
//     if (!modalContent) return;

//     modalContent.setAttribute('aria-busy', 'true');
//     modalContent.style.opacity = '0.5';
//     modalContent.style.pointerEvents = 'none';

//     const fetchUrl = new URL(url, window.location.origin);
//     fetchUrl.searchParams.set('view', 'quick-add');

//     try {
//       const response = await fetch(fetchUrl.toString());
//       if (!response.ok) {
//         console.error(`QuickAdd: Fetch failed for ${fetchUrl.toString()} with status ${response.status}`);
//         throw new Error(`Failed to fetch product: ${response.status}`);
//       }
//       const htmlText = await response.text();
//       const html = new DOMParser().parseFromString(htmlText, 'text/html');

//       const newContent = html.querySelector('[data-product-grid-content]');
//       if (newContent) {
//         morph(modalContent, newContent);
//         this.initQuickAddProgressBar();
//       } else {
//         console.error('QuickAdd: Content not found in the response', html);
//       }
//     } catch (error) {
//       console.error('Error loading product in quick add:', error);
//     } finally {
//       modalContent.removeAttribute('aria-busy');
//       modalContent.style.opacity = '';
//       modalContent.style.pointerEvents = '';
//     }
//   }
// }

// if (!customElements.get('quick-add-dialog')) {
//   customElements.define('quick-add-dialog', QuickAddDialog);
// }

/**
 * Global Quick Add API — exposes `window.quickAdd.open()` so that third-party
 * integrations (e.g. Searchspring) can trigger the theme's Quick Add modal for
 * any product without needing a theme product-card element in the DOM.
 *
 * Accepts three forms:
 *   1. Product URL path  →  window.quickAdd.open('/products/my-handle')  ← most reliable
 *   2. Numeric product ID  →  window.quickAdd.open(7815010353206)
 *   3. CSS selector string  →  window.quickAdd.open('[data-product-id="123456789"]')
 *
 * For pages where the product card is NOT in the DOM (e.g. home page),
 * pass the product URL directly (form 1) — Searchspring always has it in their feed.
 */
(function registerGlobalQuickAdd() {
  document.addEventListener('click', (e) => {
    const btn = e.target instanceof HTMLElement ? e.target.closest('.quick-add__button') : null;
    if (btn instanceof HTMLElement) {
      lastClickedQuickAddButton = btn;

      if (quickAddLoadingTimeout) {
        clearTimeout(quickAddLoadingTimeout);
      }

      btn.setAttribute('data-loading', 'true');
      btn.setAttribute('disabled', 'true');
      if (!btn.querySelector('.quick-add__spinner')) {
        btn.insertAdjacentHTML('beforeend', '<span class="quick-add__spinner" aria-hidden="true"></span>');
      }

      quickAddLoadingTimeout = setTimeout(() => {
        clearQuickAddButtonLoading(btn);
        if (lastClickedQuickAddButton === btn) lastClickedQuickAddButton = null;
        if (activeQuickAddButton === btn) activeQuickAddButton = null;
        quickAddLoadingTimeout = null;
      }, 10000);
    }
  }, true);

  /**
   * Extracts the product page URL from a product-card element or any element
   * that contains one.
   * @param {Element} element
   * @returns {string | null}
   */
  function getProductUrlFromElement(element) {
    // Walk up to a product-card if we're inside one
    const card = element.closest('product-card') || element.querySelector('product-card') || element;

    // Prefer the hidden input / anchor that the theme uses (ref="productCardLink")
    const linkInput = card.querySelector('input.product-card__link');
    if (linkInput instanceof HTMLInputElement && linkInput.value) {
      return linkInput.value;
    }

    const linkAnchor = card.querySelector('a.product-card__link');
    if (linkAnchor instanceof HTMLAnchorElement && linkAnchor.href) {
      return linkAnchor.href;
    }

    // Some integrations set data-product-url directly on the element
    const dataUrl = element.getAttribute('data-product-url') || card.getAttribute('data-product-url');
    if (dataUrl) return dataUrl;

    return null;
  }

  /**
   * Resolves the product URL from the DOM using `data-product-id`.
   * @param {string | number} productId
   * @returns {string | null}
   */
  function getProductUrlFromDom(productId) {
    const card = document.querySelector(`product-card[data-product-id="${productId}"]`);
    if (card) return getProductUrlFromElement(card);
    return null;
  }

  /**
   * Resolves a product URL from a numeric product ID via Shopify's Predictive
   * Search API, verified by EXACT numeric ID match.
   *
   * ⚠️  /recommendations/products.json is intentionally NOT used here —
   *     that endpoint returns RELATED products (never the seed product itself),
   *     which caused wrong products to open on pages like the home page.
   *
   * @param {string} productId
   * @returns {Promise<string | null>}
   */
  async function resolveProductUrlFromApi(productId) {
    try {
      const params = new URLSearchParams({
        q: productId,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[options][fields]': 'id,title,handle',
        'resources[options][unavailable_products]': 'show',
      });

      const response = await fetch(`/search/suggest.json?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        const products = data?.resources?.results?.products;
        if (Array.isArray(products)) {
          // EXACT numeric ID match only — never accept a partial search hit
          const match = products.find((p) => String(p.id) === productId);
          if (match?.url) return match.url;
          if (match?.handle) return `/products/${match.handle}`;
        }
      }

      console.warn(
        `[window.quickAdd] Could not find product ID "${productId}" via Shopify Predictive Search. ` +
        'For reliable results when the product card is not in the DOM, ' +
        "pass the product URL directly: window.quickAdd.open('/products/my-handle')"
      );
      return null;
    } catch (_) {
      console.warn(`[window.quickAdd] Network error while resolving product URL for ID: ${productId}`);
      return null;
    }
  }

  /**
   * Opens the Quick Add modal for a given product URL.
   * @param {string} productUrl
   */
  async function openQuickAddModal(productUrl) {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) {
      console.warn('[window.quickAdd] quick-add-dialog element not found in DOM. Is the Quick Add feature enabled in theme settings?');
      return;
    }

    await dialogComponent.loadProduct(productUrl);
    dialogComponent.showDialog();
  }

  /**
   * Returns true if the string looks like a product URL path or absolute URL.
   * @param {string} s
   * @returns {boolean}
   */
  function isProductUrl(s) {
    if (/^https?:\/\//i.test(s)) return true;
    // Root-relative, with optional locale prefix (e.g. /en-au/products/...)
    if (/^\/(\w{2}(-\w{2,4})?\/)?products\//i.test(s)) return true;
    return false;
  }

  /**
   * Opens the Quick Add modal for a given product.
   *
   * @param {string | number} target   One of:
   *   • A product URL  →  window.quickAdd.open('/products/my-handle')  ← most reliable
   *   • A numeric ID   →  window.quickAdd.open(7815010353206)
   *   • A CSS selector →  window.quickAdd.open('[data-product-id="123456789"]')
   * @returns {Promise<void>}
   */
  async function open(target) {
    if (target === null || target === undefined || target === '') {
      console.warn('[window.quickAdd] open() requires a product URL, ID, or CSS selector.');
      return;
    }

    const button = lastClickedQuickAddButton;
    lastClickedQuickAddButton = null;

    if (quickAddLoadingTimeout) {
      clearTimeout(quickAddLoadingTimeout);
      quickAddLoadingTimeout = null;
    }

    if (button instanceof HTMLElement) {
      button.setAttribute('data-loading', 'true');
      button.setAttribute('disabled', 'true');
      if (!button.querySelector('.quick-add__spinner')) {
        button.insertAdjacentHTML('beforeend', '<span class="quick-add__spinner" aria-hidden="true"></span>');
      }
      activeQuickAddButton = button;
    }

    try {
      const targetStr = target.toString().trim();

      // ── Branch 1: Direct product URL ─────────────────────────────────────────
      // Safest, fastest path. Recommended for Searchspring (URL is in their feed).
      // e.g. window.quickAdd.open('/products/my-surf-board')
      if (typeof target === 'string' && isProductUrl(targetStr)) {
        await openQuickAddModal(targetStr);
        return;
      }

      // ── Branch 2: Numeric product ID ─────────────────────────────────────────
      // e.g. window.quickAdd.open(7815010353206)
      if (typeof target === 'number' || /^\d+$/.test(targetStr)) {
        const productId = targetStr;

        // 2a. Fast zero-network DOM look-up
        let productUrl = getProductUrlFromDom(productId);

        // 2b. Storefront API fallback (predictive search, exact ID match only)
        if (!productUrl) {
          productUrl = await resolveProductUrlFromApi(productId);
        }

        if (!productUrl) return; // Warning already logged

        await openQuickAddModal(productUrl);
        return;
      }

      // ── Branch 3: CSS selector ────────────────────────────────────────────────
      // e.g. window.quickAdd.open('[data-product-id="7815010353206"]')
      const element = document.querySelector(targetStr);
      if (!element) {
        console.warn(`[window.quickAdd] No element matched selector: "${targetStr}"`);
        return;
      }

      // Try to read the URL directly from within the element
      let productUrl = getProductUrlFromElement(element);

      // Fall back to data-product-id on the element or its card ancestor
      if (!productUrl) {
        const card = element.closest('product-card') || element.querySelector('product-card') || element;
        const productId = card.getAttribute('data-product-id') || element.getAttribute('data-product-id');
        if (productId) {
          productUrl = getProductUrlFromDom(productId) || await resolveProductUrlFromApi(productId);
        }
      }

      if (!productUrl) {
        console.warn(`[window.quickAdd] Could not resolve a product URL from selector: "${targetStr}"`);
        return;
      }

      await openQuickAddModal(productUrl);
    } catch (error) {
      console.error('[window.quickAdd] Error during open():', error);
    } finally {
      clearQuickAddButtonLoading(button);
      if (activeQuickAddButton === button) activeQuickAddButton = null;
    }
  }

  // Register on window — guard against double-registration during HMR / re-imports
  if (!window.quickAdd) {
    window.quickAdd = { open };
  } else {
    // Merge — preserve any future methods that may have been added
    window.quickAdd.open = open;
  }
})();
